import {
  toE164Digits,
  phoneDigitsOnly,
  isValidPhoneField,
  isPhoneSentinel,
  isUsableStoredPhone,
  normalizeDialPhone,
} from "./phone";
import {
  CHANNEL_TYPES,
  isComposerOutboundChannelTipo,
  isWhatsAppQrChannelTipo,
  isWhatsAppOfficialChannelTipo,
} from "../constants/channelCatalog";
import { isWhatsappGroupConversation, resolveWhatsappGroupId } from "./whatsappQrGroupMentions";

function firstUsablePhoneRaw(...candidates) {
  for (const candidate of candidates) {
    const s = String(candidate ?? "").trim();
    if (!s || s === "-" || s === "—") continue;
    if (!isUsableStoredPhone(s)) continue;
    return s;
  }
  return "";
}

/** Telefone bruto do cadastro do cliente na solicitação (todos os aliases usados na UI/API). */
export function getClientPhoneRaw(conversation) {
  if (!conversation) return "";
  const client = conversation.client;
  return firstUsablePhoneRaw(
    conversation.celular,
    conversation.phone,
    conversation.phoneNumber,
    conversation.telefone,
    conversation.whatsappPhone,
    conversation.whatsapp,
    client?.mobile,
    client?.phone,
    client?.celular,
    client?.telephone,
    client?.telefone,
    client?.whatsappPhone,
    client?.whatsapp
  );
}

/**
 * Destino para SMS: prioriza celular (não WhatsApp).
 * Fallback só se celular estiver vazio.
 */
export function getSmsClientPhoneRaw(conversation) {
  if (!conversation) return "";
  const client = conversation.client;
  return firstUsablePhoneRaw(
    conversation.celular,
    conversation.phone,
    conversation.phoneNumber,
    conversation.telefone,
    client?.mobile,
    client?.phone,
    client?.celular,
    client?.telephone,
    client?.telefone,
    conversation.whatsappPhone,
    conversation.whatsapp,
    client?.whatsappPhone,
    client?.whatsapp
  );
}

/**
 * Telefone de envio conforme o canal.
 * SMS → celular primeiro, E.164 com '+' (compatível com a API Twilio).
 * Demais canais → WhatsApp/celular sem reformatar (comportamento histórico).
 */
export function resolveOutboundClientPhone(conversation, channelType, defaultCountry = "BR") {
  const type = String(channelType || "").trim().toLowerCase();
  if (type === "sms") {
    const raw = getSmsClientPhoneRaw(conversation);
    if (!raw) return "";
    const normalized = normalizeDialPhone(raw, defaultCountry);
    if (normalized.ok && normalized.e164) return normalized.e164;
    const digits = toE164Digits(raw, defaultCountry) || phoneDigitsOnly(raw);
    return digits ? (digits.startsWith("+") ? digits : `+${digits}`) : "";
  }

  if (isWhatsappGroupConversation(conversation)) {
    return resolveWhatsappGroupId(conversation) || "";
  }

  return (
    String(conversation?.whatsappPhone || "").trim() ||
    String(conversation?.celular || "").trim() ||
    getClientPhoneRaw(conversation) ||
    ""
  );
}

/** Dígitos do telefone do cliente na conversa (cadastro). */
export function getClientPhoneDigits(conversation) {
  return phoneDigitsOnly(getClientPhoneRaw(conversation));
}

/** E.164 sem '+' a partir do cadastro na conversa (inferência BR quando aplicável). */
export function getClientPhoneE164(conversation, defaultCountry = "BR") {
  const raw = getClientPhoneRaw(conversation);
  if (!raw) return "";
  return toE164Digits(String(raw), defaultCountry) || phoneDigitsOnly(raw);
}

export function hasClientPhoneRegistered(conversation) {
  const raw = getClientPhoneRaw(conversation);
  if (!raw) return false;
  if (isPhoneSentinel(raw)) return false;
  if (isValidPhoneField(raw)) return true;
  const e164 = toE164Digits(raw);
  return e164.length >= 10;
}

/** E-mail do cliente na conversa (cadastro). */
export function getClientEmail(conversation) {
  if (!conversation) return "";
  const client = conversation.client;
  const raw =
    conversation.email ??
    conversation.clientEmail ??
    client?.email ??
    "";
  return String(raw).trim();
}

export function hasClientEmailRegistered(conversation) {
  const email = getClientEmail(conversation);
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const PHONE_REQUIRED_CHANNEL_TYPES = new Set(["WhatsApp", "SMS", "Phone", "Telefone", "RCS"]);
const EMAIL_REQUIRED_CHANNEL_TYPES = new Set(["Email"]);

/**
 * Canais que só permitem envio quando a solicitação já tem contexto nesse canal
 * (origem / thread). Ex.: comentário do Facebook exige post/comentário; Website
 * e Messenger exigem a conversa naquele meio — não dá para “iniciar” do zero só
 * porque o canal existe no workspace.
 */
const THREAD_BOUND_CHANNEL_TYPES = new Set([
  "Facebook Comentários",
  "Instagram Comentários",
  "Messenger",
  "Instagram",
  "Wordpress",
  "Website",
  "App",
  "Ecommerce",
  "Ads",
  "Portal do cliente",
  "Reclame Aqui",
  "Chat no site",
]);

const THREAD_BOUND_CHANNEL_TIPOS = new Set([
  CHANNEL_TYPES.FB_MESSENGER,
  CHANNEL_TYPES.FB_COMMENTS,
  CHANNEL_TYPES.IG_MESSENGER,
  CHANNEL_TYPES.IG_COMMENTS,
  CHANNEL_TYPES.WORDPRESS,
  CHANNEL_TYPES.WEBSITE_VIACAO,
  CHANNEL_TYPES.APP_IOS,
  CHANNEL_TYPES.APP_ANDROID,
  CHANNEL_TYPES.ECOMMERCE,
  CHANNEL_TYPES.GOOGLE_ADS,
  CHANNEL_TYPES.META_ADS,
  CHANNEL_TYPES.XPRO_CLIENTE,
  CHANNEL_TYPES.RECLAME_AQUI,
  CHANNEL_TYPES.SITE_WEBCHAT,
]);

export function channelTypeRequiresClientPhone(channelType) {
  if (channelType == null || channelType === "") return false;
  return PHONE_REQUIRED_CHANNEL_TYPES.has(String(channelType));
}

export function channelTypeRequiresClientEmail(channelType) {
  if (channelType == null || channelType === "") return false;
  return EMAIL_REQUIRED_CHANNEL_TYPES.has(String(channelType));
}

/** Envio externo que exige telefone (inclui retry quando `channel` traz o rótulo do canal). */
export function outboundChannelRequiresClientPhone(channel, channelType) {
  return (
    channelTypeRequiresClientPhone(channelType) || channelTypeRequiresClientPhone(channel)
  );
}

/** Grupo WhatsApp QR envia pelo JID (`id_interno`), não pelo celular do cadastro. */
export function shouldSkipClientPhoneForWhatsappGroupSend({
  conversation,
  messages,
  channelTipo,
  channelType,
} = {}) {
  const history = Array.isArray(messages) ? messages : conversation?.messages;
  if (!isWhatsappGroupConversation(conversation, history)) return false;
  if (isWhatsAppOfficialChannelTipo(channelTipo)) return false;
  if (isWhatsAppQrChannelTipo(channelTipo)) return true;
  const type = String(channelType || "");
  return !type || type === "WhatsApp" || type === "App";
}

export function getConversationOriginChannelId(conversation) {
  if (!conversation) return null;
  const id =
    conversation.channelId ?? conversation.id_origem ?? conversation.channel_id ?? null;
  if (id == null || id === "" || id === 0 || id === "0") return null;
  return id;
}

export function isThreadBoundChannel(channelType, channelTipo = null) {
  const tipo = Number(channelTipo);
  if (Number.isFinite(tipo) && THREAD_BOUND_CHANNEL_TIPOS.has(tipo)) return true;
  const type = channelType != null && channelType !== "" ? String(channelType) : "";
  return THREAD_BOUND_CHANNEL_TYPES.has(type);
}

/**
 * True se a solicitação (ou o histórico informado) já usa esse canal de origem/thread.
 */
export function conversationHasChannelContext(conversation, channelId, messages = null) {
  if (channelId == null || channelId === "") return false;
  const key = String(channelId);

  const originId = getConversationOriginChannelId(conversation);
  if (originId != null && String(originId) === key) return true;

  if (!Array.isArray(messages) || !messages.length) return false;
  return messages.some((m) => {
    const mid =
      m?.messageChannelId ??
      m?.channelId ??
      m?.id_origem ??
      m?.originId ??
      m?.origin_id ??
      null;
    return mid != null && mid !== "" && String(mid) === key;
  });
}

/**
 * Indica se o canal selecionado pode ser usado para falar com o cliente com os dados atuais da solicitação.
 * Anotações internas e WhatsApp pessoal sempre retornam true.
 * @param {object|null} conversation
 * @param {string|number} channelId
 * @param {string|null|undefined} channelType
 * @param {number|string|null|undefined} [channelTipo] tipo numérico (origensdeleads.tipo)
 * @param {object[]|null} [messages] histórico opcional para detectar canais já usados na thread
 */
export function canUseClientChannelForConversation(
  conversation,
  channelId,
  channelType,
  channelTipo = null,
  messages = null
) {
  if (!conversation || conversation.kind === "personal") return true;
  if (channelId == null || channelId === "") return false;

  const channelKey = String(channelId);
  if (channelKey === "internal_note") return true;
  if (channelKey === "personal_whatsapp") {
    return !isWhatsappGroupConversation(conversation, messages);
  }

  const tipoNum =
    channelTipo != null && channelTipo !== "" ? Number(channelTipo) : NaN;

  if (isWhatsappGroupConversation(conversation, messages)) {
    if (isWhatsAppQrChannelTipo(tipoNum)) {
      return conversationHasChannelContext(conversation, channelId, messages);
    }
    if (isWhatsAppOfficialChannelTipo(tipoNum)) return false;
    if (channelTypeRequiresClientPhone(String(channelType || "")) && !hasClientPhoneRegistered(conversation)) {
      return false;
    }
    if (channelTypeRequiresClientEmail(String(channelType || "")) && !hasClientEmailRegistered(conversation)) {
      return false;
    }
    if (channelTypeRequiresClientPhone(String(channelType || ""))) {
      return hasClientPhoneRegistered(conversation);
    }
    if (isThreadBoundChannel(String(channelType || ""), channelTipo)) {
      return conversationHasChannelContext(conversation, channelId, messages);
    }
    return false;
  }
  // Reclame Aqui: outbound só no modo API (avaliado no ChannelSelector via
  // isComposerOutboundChannel). Aqui só bloqueamos tipos claramente inbound-only.
  if (
    Number.isFinite(tipoNum) &&
    tipoNum !== CHANNEL_TYPES.RECLAME_AQUI &&
    !isComposerOutboundChannelTipo(tipoNum)
  ) {
    return false;
  }

  const type = channelType != null && channelType !== "" ? String(channelType) : "";
  // Origens genéricas (API label "Xpro" / tipo DEFAULT) não enviam mensagem.
  if (type === "Xpro" && !Number.isFinite(tipoNum)) return false;
  if ((!type || type === "Xpro") && channelTipo == null) return false;

  if (isThreadBoundChannel(type, channelTipo)) {
    return conversationHasChannelContext(conversation, channelId, messages);
  }

  if (channelTypeRequiresClientPhone(type) && !hasClientPhoneRegistered(conversation)) {
    return false;
  }
  if (channelTypeRequiresClientEmail(type) && !hasClientEmailRegistered(conversation)) {
    return false;
  }

  return true;
}
